import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LocalCacheModel } from '../../models/local-cache.model';
import { LocalStorageKeys } from '../../models/local-storage-keys.model';
import { LocalCacheService } from '../../services/local-cache.service';
import { PeriodService } from '../../services/period.service';
import { FilterPeriods } from '../action/period.action';
import { ComplexPeriodModel } from './../../models/complex-period.model';
import { PeriodFilter } from './../../models/period-filter.model';
import { PeriodModel } from './../../models/period.model';
import { FilterService } from './../../services/filter.service';
import { LocalizationService } from './../../services/localization.service';
import { NotificationService } from './../../services/notification.service';
import {
  AddPeriod,
  EditPeriod,
  ImportPeriods,
  RemovePeriod,
} from './../action/period.action';

export interface PeriodStateModel {
  periods: PeriodModel[];
  periodsTotal: number;
  filter?: PeriodFilter;
}

@State<PeriodStateModel>({
  name: 'periodstate',
  defaults: {
    periods: [],
    periodsTotal: 0,
  },
})
@Injectable()
export class PeriodState {
  constructor(
    private periodService: PeriodService,
    private store: Store,
    private filterService: FilterService,
    private notificationService: NotificationService,
    private localizationService: LocalizationService,
    private localCacheService: LocalCacheService
  ) {}

  @Selector()
  static periods(ctx: PeriodStateModel) {
    return ctx.periods;
  }

  @Selector()
  static periodsTotal(ctx: PeriodStateModel) {
    return ctx.periodsTotal;
  }

  @Action(FilterPeriods)
  async filterPeriods(
    ctx: StateContext<PeriodStateModel>,
    action: FilterPeriods
  ) {
    const data = await this.periodService
      .getAll(action.params, action.filter)
      .toPromise();

    console.log('period data: ', data);

    if (data) {
      ctx.patchState({
        periods: data.data,
        periodsTotal: data.total,
        filter: action.filter,
      });
    }
  }

  @Action(AddPeriod)
  async addPeriod(ctx: StateContext<PeriodStateModel>, action: AddPeriod) {
    this.localCacheService.remove(LocalStorageKeys.PeriodsKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.periodService.add(action.model).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterPeriods(this.filterService.generateHttpParams(), filter)
      );

      this.notificationService.info(
        this.localizationService.translate('added'),
        this.localizationService.translate('successfully-added')
      );
    }
  }

  @Action(EditPeriod)
  async editPeriod(ctx: StateContext<PeriodStateModel>, action: EditPeriod) {
    this.localCacheService.remove(LocalStorageKeys.PeriodsKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.periodService.update(action.model).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterPeriods(this.filterService.generateHttpParams(), filter)
      );

      this.notificationService.info(
        this.localizationService.translate('edited'),
        this.localizationService.translate('successfully-edited')
      );
    }
  }

  @Action(RemovePeriod)
  async removePeriod(
    ctx: StateContext<PeriodStateModel>,
    action: RemovePeriod
  ) {
    this.localCacheService.remove(LocalStorageKeys.PeriodsKey);
    const filter = ctx.getState().filter;
    const data = await this.periodService.remove(action.modelId).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterPeriods(this.filterService.generateHttpParams(), filter)
      );

      this.notificationService.info(
        this.localizationService.translate('removed'),
        this.localizationService.translate('successfully-removed')
      );
    }
  }

  @Action(ImportPeriods)
  async importPeriods(
    ctx: StateContext<PeriodStateModel>,
    action: ImportPeriods
  ) {
    this.localCacheService.remove(LocalStorageKeys.PeriodsKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;

    const data = await this.periodService.import(action.data).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterPeriods(this.filterService.generateHttpParams(), filter)
      );
    }
  }
}
